import { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { CreditCard, Package, Shield, MapPin, Plus, Edit2, Trash2, Check } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import AddressForm from '@/components/AddressForm';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Checkout = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [cartItems, setCartItems] = useState([]);
  const [addresses, setAddresses] = useState([]);
  const [selectedAddressId, setSelectedAddressId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [productMedia, setProductMedia] = useState({});
  const [showAddressForm, setShowAddressForm] = useState(false);
  const [editingAddress, setEditingAddress] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchCart();
    fetchAddresses();
  }, []);

  const fetchCart = async () => {
    try {
      const response = await axios.get(`${API}/cart`, { withCredentials: true });
      if (response.data.length === 0) {
        toast.error('Your cart is empty');
        navigate('/shop');
        return;
      }
      setCartItems(response.data);
      
      response.data.forEach(item => {
        if (item.product_id) {
          fetchProductMedia(item.product_id);
        }
      });
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error('Please login to checkout');
        navigate('/cart');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchAddresses = async () => {
    try {
      const response = await axios.get(`${API}/addresses`, { withCredentials: true });
      if (response.data.success) {
        setAddresses(response.data.addresses);
        
        // Auto-select default shipping address
        const defaultAddr = response.data.addresses.find(addr => addr.is_default_shipping);
        if (defaultAddr) {
          setSelectedAddressId(defaultAddr.id);
        }
      }
    } catch (error) {
      console.error('Failed to fetch addresses:', error);
    }
  };

  const fetchProductMedia = async (productId) => {
    if (productMedia[productId]) return;

    try {
      const response = await axios.get(`${API}/products/${productId}/media`);
      if (response.data.success && response.data.media.length > 0) {
        const mediaList = response.data.media;
        const primary = mediaList.find(media => media.is_primary && media.file_type === 'image');
        const images = mediaList.filter(media => media.file_type === 'image');
        
        setProductMedia(prev => ({
          ...prev,
          [productId]: primary || images[0]
        }));
      }
    } catch (error) {
      console.error('Failed to fetch product media:', error);
    }
  };

  const handleDeleteAddress = async (addressId) => {
    if (!window.confirm('Are you sure you want to delete this address?')) return;

    try {
      await axios.delete(`${API}/addresses/${addressId}`, { withCredentials: true });
      toast.success('Address deleted');
      fetchAddresses();
      
      if (selectedAddressId === addressId) {
        setSelectedAddressId(null);
      }
    } catch (error) {
      toast.error('Failed to delete address');
    }
  };

  const handleSetDefault = async (addressId) => {
    try {
      await axios.patch(`${API}/addresses/${addressId}/default`, 
        { address_type: 'shipping' },
        { withCredentials: true }
      );
      toast.success('Default address updated');
      fetchAddresses();
    } catch (error) {
      toast.error('Failed to update default address');
    }
  };

  const handleAddressSubmit = async (addressData) => {
    try {
      if (editingAddress) {
        await axios.put(`${API}/addresses/${editingAddress.id}`, addressData, { withCredentials: true });
        toast.success('Address updated');
      } else {
        const response = await axios.post(`${API}/addresses`, addressData, { withCredentials: true });
        toast.success('Address added');
        
        // Auto-select newly added address
        if (response.data.address_id) {
          setSelectedAddressId(response.data.address_id);
        }
      }
      
      setShowAddressForm(false);
      setEditingAddress(null);
      fetchAddresses();
    } catch (error) {
      toast.error('Failed to save address');
    }
  };

  const calculateTotal = () => {
    return cartItems.reduce((total, item) => {
      const price = item.product?.discount_price || item.product?.price || 0;
      return total + price * item.quantity;
    }, 0);
  };

  const handleContinueToReview = () => {
    if (!selectedAddressId) {
      toast.error('Please select a delivery address');
      return;
    }
    setCurrentStep(2);
  };

  const handlePayment = async () => {
    setProcessing(true);
    try {
      // Create order and get Instamojo payment URL
      const orderResponse = await axios.post(`${API}/orders/create`, {
        address_id: selectedAddressId
      }, { withCredentials: true });
  
      console.log(orderResponse);
      const { payment_url, order_id, amount } = orderResponse.data;

      if (!payment_url) {
        throw new Error('Failed to get payment URL');
      }

      // Store order_id in sessionStorage for callback verification
      sessionStorage.setItem('pending_order_id', order_id);

      // Redirect to Instamojo payment page
      window.location.href = payment_url;

    } catch (error) {
      console.error('Payment error:', error);
      toast.error(error.response?.data?.detail || 'Failed to initiate payment');
      setProcessing(false);
    }
  };

  const selectedAddress = addresses.find(addr => addr.id === selectedAddressId);

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-red-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black">
      <Navbar />
      
      <div className="pt-24 px-4 sm:px-6 lg:px-8 pb-20">
        <div className="max-w-6xl mx-auto">
          {/* Progress Steps */}
          <div className="flex items-center justify-center mb-12">
            <div className="flex items-center">
              <div className={`flex items-center ${currentStep >= 1 ? 'text-red-500' : 'text-gray-500'}`}>
                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${currentStep >= 1 ? 'bg-red-500 text-white' : 'bg-gray-700'}`}>
                  {currentStep > 1 ? <Check size={20} /> : '1'}
                </div>
                <span className="ml-2 hidden sm:block">Address</span>
              </div>
              
              <div className={`w-20 h-1 mx-4 ${currentStep >= 2 ? 'bg-red-500' : 'bg-gray-700'}`}></div>
              
              <div className={`flex items-center ${currentStep >= 2 ? 'text-red-500' : 'text-gray-500'}`}>
                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${currentStep >= 2 ? 'bg-red-500 text-white' : 'bg-gray-700'}`}>
                  2
                </div>
                <span className="ml-2 hidden sm:block">Review & Pay</span>
              </div>
            </div>
          </div>

          {/* Step 1: Address Selection */}
          {currentStep === 1 && (
            <div>
              <h1 className="text-4xl font-bold mb-8">
                <span className="accent-red">Select Delivery Address</span>
              </h1>

              {showAddressForm ? (
                <AddressForm 
                  address={editingAddress}
                  onSubmit={handleAddressSubmit}
                  onCancel={() => {
                    setShowAddressForm(false);
                    setEditingAddress(null);
                  }}
                />
              ) : (
                <>
                  <div className="grid md:grid-cols-2 gap-6 mb-8">
                    {addresses.map(address => (
                      <div 
                        key={address.id}
                        className={`glass-effect p-6 rounded-xl cursor-pointer transition ${
                          selectedAddressId === address.id ? 'ring-2 ring-red-500' : ''
                        }`}
                        onClick={() => setSelectedAddressId(address.id)}
                      >
                        <div className="flex justify-between items-start mb-4">
                          <div className="flex items-center">
                            <div className={`w-5 h-5 rounded-full border-2 mr-3 flex items-center justify-center ${
                              selectedAddressId === address.id ? 'border-red-500 bg-red-500' : 'border-gray-500'
                            }`}>
                              {selectedAddressId === address.id && <Check size={14} className="text-white" />}
                            </div>
                            <div>
                              {address.label && (
                                <span className="text-sm font-semibold text-red-500">{address.label}</span>
                              )}
                              {address.is_default_shipping && (
                                <span className="ml-2 text-xs bg-red-500/20 text-red-500 px-2 py-1 rounded">Default</span>
                              )}
                            </div>
                          </div>
                          
                          <div className="flex space-x-2">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setEditingAddress(address);
                                setShowAddressForm(true);
                              }}
                              className="text-gray-400 hover:text-white"
                            >
                              <Edit2 size={18} />
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteAddress(address.id);
                              }}
                              className="text-gray-400 hover:text-red-500"
                            >
                              <Trash2 size={18} />
                            </button>
                          </div>
                        </div>

                        <div className="text-sm space-y-1">
                          <p className="font-semibold">{address.recipient_name}</p>
                          <p className="text-gray-400">
                            {address.address_line1}
                            {address.address_line2 && `, ${address.address_line2}`}
                          </p>
                          <p className="text-gray-400">
                            {address.city}, {address.state} - {address.postal_code}
                          </p>
                          {address.phone && <p className="text-gray-400">Phone: {address.phone}</p>}
                        </div>

                        {!address.is_default_shipping && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleSetDefault(address.id);
                            }}
                            className="mt-4 text-sm text-red-500 hover:underline"
                          >
                            Set as default
                          </button>
                        )}
                      </div>
                    ))}

                    {/* Add New Address Card */}
                    <div 
                      className="glass-effect p-6 rounded-xl cursor-pointer hover:bg-white/5 transition flex items-center justify-center min-h-[200px]"
                      onClick={() => setShowAddressForm(true)}
                    >
                      <div className="text-center">
                        <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                          <Plus size={32} className="text-red-500" />
                        </div>
                        <p className="font-semibold">Add New Address</p>
                      </div>
                    </div>
                  </div>

                  <div className="flex justify-between">
                    <button
                      onClick={() => navigate('/cart')}
                      className="px-6 py-3 border border-white/20 rounded-lg hover:bg-white/5 transition"
                    >
                      Back to Cart
                    </button>
                    <button
                      onClick={handleContinueToReview}
                      disabled={!selectedAddressId}
                      className="btn-primary px-8 py-3 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Continue to Review
                    </button>
                  </div>
                </>
              )}
            </div>
          )}

          {/* Step 2: Review & Payment */}
          {currentStep === 2 && (
            <div>
              <h1 className="text-4xl font-bold mb-8">
                <span className="accent-red">Review Order</span>
              </h1>

              <div className="grid lg:grid-cols-2 gap-8">
                {/* Left: Address & Items */}
                <div>
                  {/* Delivery Address */}
                  <div className="glass-effect p-6 rounded-xl mb-6">
                    <div className="flex justify-between items-start mb-4">
                      <h2 className="text-xl font-bold flex items-center">
                        <MapPin size={20} className="mr-2 text-red-500" />
                        Delivery Address
                      </h2>
                      <button
                        onClick={() => setCurrentStep(1)}
                        className="text-sm text-red-500 hover:underline"
                      >
                        Change
                      </button>
                    </div>
                    {selectedAddress && (
                      <div className="text-sm space-y-1">
                        <p className="font-semibold">{selectedAddress.recipient_name}</p>
                        <p className="text-gray-400">
                          {selectedAddress.address_line1}
                          {selectedAddress.address_line2 && `, ${selectedAddress.address_line2}`}
                        </p>
                        <p className="text-gray-400">
                          {selectedAddress.city}, {selectedAddress.state} - {selectedAddress.postal_code}
                        </p>
                        {selectedAddress.phone && <p className="text-gray-400">Phone: {selectedAddress.phone}</p>}
                      </div>
                    )}
                  </div>

                  {/* Order Items */}
                  <div className="glass-effect p-6 rounded-xl mb-6">
                    <h2 className="text-2xl font-bold mb-6">Order Items</h2>
                    <div className="space-y-4">
                      {cartItems.map(item => {
                        const media = productMedia[item.product_id];
                        const imageUrl = media?.file_url || item.product?.image_url;

                        return (
                          <div key={item.id} className="flex items-center space-x-4 pb-4 border-b border-white/10 last:border-0">
                            <Link to={`/product/${item.product_id}`}>
                              <img 
                                src={imageUrl} 
                                alt={item.product?.name} 
                                className="w-16 h-16 object-cover rounded-lg hover:opacity-80 transition cursor-pointer"
                              />
                            </Link>
                            <div className="flex-1">
                              <Link to={`/product/${item.product_id}`}>
                                <h3 className="font-semibold hover:text-red-500 transition cursor-pointer">{item.product?.name}</h3>
                              </Link>
                              <p className="text-sm text-gray-400">Qty: {item.quantity}</p>
                            </div>
                            <div className="flex items-center gap-2">
                              <p className="text-2xl font-bold accent-red" data-testid={`item-price-${item.id}`}>
                                ₹{(item.product?.discount_price || item.product?.price).toLocaleString()}
                              </p>
                              {item.product?.discount_price && (
                                <p className="text-gray-400 line-through text-sm">
                                  ₹{item.product?.price.toLocaleString()}
                                </p>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Trust Badges */}
                  <div className="grid grid-cols-3 gap-4">
                    <div className="glass-effect p-4 rounded-lg text-center">
                      <Shield className="mx-auto mb-2 text-red-500" size={24} />
                      <p className="text-xs text-gray-400">Secure Payment</p>
                    </div>
                    <div className="glass-effect p-4 rounded-lg text-center">
                      <Package className="mx-auto mb-2 text-red-500" size={24} />
                      <p className="text-xs text-gray-400">Fast Delivery</p>
                    </div>
                    <div className="glass-effect p-4 rounded-lg text-center">
                      <CreditCard className="mx-auto mb-2 text-red-500" size={24} />
                      <p className="text-xs text-gray-400">Easy Returns</p>
                    </div>
                  </div>
                </div>

                {/* Right: Payment Summary */}
                <div>
                  <div className="glass-effect p-6 rounded-xl sticky top-24">
                    <h2 className="text-2xl font-bold mb-6">Payment Summary</h2>
                    
                    <div className="space-y-4 mb-6">
                      <div className="flex justify-between">
                        <span className="text-gray-400">Subtotal</span>
                        <span className="font-semibold">₹{calculateTotal().toLocaleString()}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-400">Shipping</span>
                        <span className="font-semibold text-green-500">FREE</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-400">Tax</span>
                        <span className="font-semibold">Included</span>
                      </div>
                      <div className="border-t border-white/10 pt-4">
                        <div className="flex justify-between">
                          <span className="text-xl font-bold">Total</span>
                          <span className="text-3xl font-bold accent-red">₹{calculateTotal().toLocaleString()}</span>
                        </div>
                      </div>
                    </div>
                      {/* //commented for live, uncomment on local 18-05-26 */}
                     
                    {/* <button
                      onClick={handlePayment}
                      disabled={processing}
                      className="w-full btn-primary py-4 text-lg disabled:opacity-50 disabled:cursor-not-allowed mb-4"
                    >
                      {processing ? 'Redirecting to payment...' : 'Pay with Instamojo'}
                    </button> */}

                    <button
                      onClick={() => setCurrentStep(1)}
                      className="w-full px-6 py-3 border border-white/20 rounded-lg hover:bg-white/5 transition"
                    >
                      Back to Address
                    </button>

                    <div className="mt-6 text-center">
                      <p className="text-sm text-gray-400 mb-2">Powered by Instamojo</p>
                      <div className="flex justify-center space-x-2 text-xs text-gray-500">
                        <span>UPI</span> • <span>Cards</span> • <span>NetBanking</span> • <span>Wallets</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default Checkout;